import { css } from '@emotion/react';
import {
  ExportReportMapComponent, type ExportReportMapProps,
} from '~/exportReport/map/exportReportMap.component';
import {
  ExportReportSummaryComponent, type ExportReportSummaryProps,
} from '~/exportReport/summary/exportReportSummary.component';
import { ExportReportTableComponent } from '~/exportReport/table/exportReportTable.component';
import { ExportReportTableDataRowComponent } from '~/exportReport/table/exportReportTableDataRow.component';
import { ExportReportTableEmptyRowComponent } from '~/exportReport/table/exportReportTableEmptyRow.component';
import { ExportReportTableHeaderComponent } from '~/exportReport/table/exportReportTableHeader.component';
import { ExportReportTableTitleRowComponent } from '~/exportReport/table/exportReportTableTitleRow.component';
import type { ReportTemplate } from '~/exportReport/template/reportTemplate';
import { isReportFormulaIsTooComplex } from '~/exportReport/template/reportTemplate.helpers';
import { getReportFormulaValue } from '~/exportReport/templateRenderer/templateRenderer.helpers';

export type ReportTableColumn = {
  id: string;
  heading: string;
  secondaryHeading: string;
};

type ExportReportTemplateRendererProps = Readonly<{
  template: ReportTemplate;
  tableColumns: ReadonlyArray<ReportTableColumn>;
  summaryProps: ExportReportSummaryProps;
  onMapLoaded?: () => void;
  mapProps: Pick<ExportReportMapProps, 'type' | 'center' | 'paths' | 'css'>;
  getDemographicLabel: (demographicId: string) => string | null;
  getDemographicValue: (columnId: string, demographicId: string) => number | null;
  isPreviewMode?: boolean;
}>;

const tableStyle = css({
  marginTop: 10,
});

const mapsWrapperStyle = css({
  width: '100%',
  height: 300,
  breakInside: 'avoid',
  display: 'flex',
  gap: 10,
  justifyContent: 'space-between',
  marginTop: 10,
});

const tableTitleStyle = css({
  display: 'block',
  fontSize: 18,
  fontWeight: 700,
  textTransform: 'uppercase',
  margin: '20px 0 8px',
});

const pageBreakStyle = css({
  breakAfter: 'page',
});

// TODO: remove after development of PDF Report is finished
const ADD_DEMOGRAPHIC_ID_IN_TITLE = false;

export const ExportReportTemplateRendererComponent = ({
  template, summaryProps, onMapLoaded, mapProps, tableColumns, getDemographicLabel, getDemographicValue, isPreviewMode,
}: ExportReportTemplateRendererProps) => {
  return (
    <>
      <ExportReportSummaryComponent {...summaryProps} />

      {template.items.map((templateItem, index) => {

        switch (templateItem.type) {
          case 'map': {
            return (
              <div key={`maps-${index}`} css={mapsWrapperStyle}>
                <ExportReportMapComponent
                  onMapLoaded={onMapLoaded}
                  id={`map${index}_roadmap`}
                  mapType="roadmap"
                  {...mapProps}
                />

                <ExportReportMapComponent
                  id={`map${index}_hybrid`}
                  mapType="hybrid"
                  {...mapProps}
                />
              </div>
            );
          }

          case 'page-break': {
            return (
              <div key={`page-break-${index}`} css={pageBreakStyle} />
            );
          }

          case 'line-break': {
            return (
              <div key={`line-break-${index}`}>
                <br />
              </div>
            );
          }

          case 'title': {
            return (
              <div key={`title-${index}`} css={tableTitleStyle}>{templateItem.content}</div>
            );
          }

          case 'table': {
            return (
              <ExportReportTableComponent key={`table-${index}`} css={tableStyle}>
                <ExportReportTableHeaderComponent
                  label={templateItem.title}
                  columnNames={tableColumns}
                />

                <tbody>
                  {templateItem.content.map((templateRow, index) => {
                    if (templateRow.type === 'data-row') {
                      if (templateRow.formula && isReportFormulaIsTooComplex(templateRow.formula)) {
                        return null;
                      }

                      const values = tableColumns.map(column => {
                        if (isPreviewMode) {
                          return templateRow.id;
                        }

                        if (templateRow.formula) {
                          const getValue = (demographicId: string) => getDemographicValue(column.id, demographicId);
                          const formulaValue = getReportFormulaValue(templateRow.formula, getValue);
                          if (formulaValue === null) {
                            return '';
                          }

                          return templateRow.formula.endsWith('%') ? `${formulaValue}%` : formulaValue;
                        }
                        else {
                          return getDemographicValue(column.id, templateRow.id)?.toString() ?? '';
                        }
                      });

                      const previewModeLabel = (templateRow.custom_label ? `${templateRow.custom_label} (${templateRow.id})` : `Label for ${templateRow.id}`);
                      let regularLabel = templateRow.custom_label ?? getDemographicLabel(templateRow.id) ?? '';
                      if (ADD_DEMOGRAPHIC_ID_IN_TITLE) {
                        regularLabel += ` | ${templateRow.id}`;
                      }

                      const label = isPreviewMode ? previewModeLabel : regularLabel;

                      return (
                        <ExportReportTableDataRowComponent
                          isBold={templateRow.style === 'bold'}
                          key={`data-row${index}`}
                          label={label}
                          values={values}
                        />
                      );
                    }

                    if (templateRow.type === 'title-row') {
                      return (
                        <ExportReportTableTitleRowComponent
                          key={`title-row-${index}`}
                          title={templateRow.content}
                        />
                      );
                    }

                    if (templateRow.type === 'empty-row') {
                      return (
                        <ExportReportTableEmptyRowComponent key={`empty-row-${index}`} />
                      );
                    }

                    return null;
                  })}
                </tbody>
              </ExportReportTableComponent>
            );
          }

          default:
            return null;
        }
      })}
    </>
  );
};
